import { MarkdownContent } from '@/components/shared/MarkdownContent';
import type { TutorialStep } from '@/config/objects/release-notes';
import { useFeatureTour } from '@/hooks/useFeatureTour';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  formatDate,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQueryClient } from '@tanstack/react-query';
import { PlayCircle } from 'lucide-react';
import { useEffect, useRef } from 'react';

/** The subset of a release doc surfaced in the What's New flow. */
export interface WhatsNewDoc {
  id: string;
  title: string;
  summary?: string | null;
  body?: string | null;
  tutorialSteps?: TutorialStep[] | null;
  publishedAt?: string | null;
}

interface WhatsNewDialogProps {
  /** The doc to show, or null when nothing is selected. */
  doc: WhatsNewDoc | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Reads a single release doc in a modal. Opening the dialog for a doc marks it
 * viewed exactly once (idempotent on the server) and refreshes the What's New
 * badge. If the doc carries tutorial steps, a "Show me" button closes the
 * dialog and launches the guided tour.
 */
export function WhatsNewDialog({ doc, open, onOpenChange }: WhatsNewDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { runFeatureTour } = useFeatureTour();
  // Track which doc id we've already marked viewed for this open cycle so a
  // re-render doesn't fire a second POST.
  const viewedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !doc) {
      viewedRef.current = null;
      return;
    }
    if (viewedRef.current === doc.id) return;
    viewedRef.current = doc.id;

    apiClient
      .post(`/release-notes/${doc.id}/view`, {})
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['whats-new'] });
      })
      .catch(() => {
        // Marking viewed is best-effort; a failure just means the badge
        // lingers until the next successful view.
      });
  }, [open, doc, apiClient, queryClient]);

  const steps = doc?.tutorialSteps ?? [];

  const handleShowMe = () => {
    onOpenChange(false);
    void runFeatureTour(steps);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{doc?.title}</DialogTitle>
          {doc?.publishedAt && (
            <DialogDescription>
              Published {formatDate(doc.publishedAt)}
            </DialogDescription>
          )}
        </DialogHeader>

        {doc?.summary && (
          <p className="text-sm text-muted-foreground">{doc.summary}</p>
        )}
        {doc?.body && <MarkdownContent content={doc.body} />}

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {steps.length > 0 && (
            <Button type="button" icon={PlayCircle} onClick={handleShowMe}>
              Show me
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
